import React from "react";
import {
  AbsoluteFill,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Sfx } from "../../b2b/ui/Sfx";
import { LOOK } from "../../b2c/look";
import { Struck } from "../../b2c/ui/bits";
import { Words } from "../../b2c/ui/Words";
import { useMerchant } from "../copy";
import type { SceneProps } from "../timing";
import { shot } from "../merchants";
import { useStage } from "../stage";
import { Notification } from "../ui/Chat";
import { Backdrop, cardAspect, PhotoCard } from "../ui/Photo";

/**
 * Local frames. The promise follows the notification quickly so its last
 * line ("transferable to your bank") still gets reading time before the
 * cut; strike and bank chip stay 8 frames apart (one accent at a time).
 */
const NOTIF = 6;
const LINE1 = 16;
const LINE2 = 22;
const STRIKE = 36;
const BANK = 44;

/**
 * Payoff — the moment it works: the product, and a Frak notification on
 * top of it ("your link made a sale", attributed to the brand, no amount).
 * Then the B2C promise, same words as the landing's /vs pages: real cash,
 * not points, transferable to your bank.
 */
export const Payoff: React.FC<SceneProps> = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { m, t, copy } = useMerchant();
  const stage = useStage();
  const img = shot(m, 2);

  const wide = stage.shape === "wide";
  const G = {
    tall: { cardW: 800, notifW: 900, head: 92 },
    feed: { cardW: 640, notifW: 860, head: 76 },
    square: { cardW: 500, notifW: 780, head: 64 },
    wide: { cardW: 820, notifW: 860, head: 96 },
  }[stage.shape];
  const cardH = G.cardW / cardAspect(img);
  const bank = spring({
    frame: frame - BANK,
    fps,
    config: { damping: 13, stiffness: 210, mass: 0.6 },
  });
  const struck = spring({
    frame: frame - LINE2 - 6,
    fps,
    config: { damping: 20, stiffness: 190 },
  });

  const product = (
    <div
      style={{
        position: "relative",
        width: G.notifW,
        height: cardH + 130,
        flex: "none",
      }}
    >
      <PhotoCard
        img={img}
        x={G.notifW / 2}
        y={130 + cardH / 2}
        width={G.cardW}
        at={0}
        rotate={2}
        from={[0, 0]}
      />
      <div style={{ position: "absolute", left: 0, top: 0 }}>
        <Notification
          at={NOTIF}
          width={G.notifW}
          app={copy.payoff.app}
          time={copy.payoff.time}
          title={copy.payoff.title}
          body={t(copy.payoff.body)}
        />
      </div>
    </div>
  );

  const promise = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: wide ? "flex-start" : "center",
        gap: 10,
        color: LOOK.white,
      }}
    >
      <Words
        text={copy.payoff.line1}
        start={LINE1}
        fontSize={G.head}
        color={LOOK.white}
      />
      <div
        style={{ display: "flex", alignItems: "baseline", gap: G.head * 0.25 }}
      >
        <Words
          text={copy.payoff.line2}
          start={LINE2}
          fontSize={G.head}
          color={LOOK.white}
        />
        <span
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: G.head,
            letterSpacing: -G.head * 0.035,
            opacity: struck,
          }}
        >
          <Struck at={STRIKE} thickness={G.head * 0.12} dimTo={0.55}>
            {copy.payoff.struck}
          </Struck>
        </span>
      </div>
      <div
        style={{
          marginTop: G.head * 0.3,
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: `${G.head * 0.18}px ${G.head * 0.4}px`,
          borderRadius: 999,
          background: LOOK.white,
          color: LOOK.ink,
          fontFamily: LOOK.body,
          fontWeight: LOOK.semi,
          fontSize: G.head * 0.4,
          scale: bank,
        }}
      >
        <span style={{ fontSize: G.head * 0.5, lineHeight: 1 }}>🏦</span>
        {copy.payoff.bank}
      </div>
    </div>
  );

  return (
    <AbsoluteFill>
      <Backdrop src={img.src} dim={0.5} />
      {wide ? (
        <AbsoluteFill
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `0 ${stage.safe.right}px 0 ${stage.safe.left}px`,
          }}
        >
          {product}
          {promise}
        </AbsoluteFill>
      ) : (
        <AbsoluteFill
          style={{
            alignItems: "center",
            justifyContent: "center",
            gap: stage.shape === "square" ? 24 : 48,
            padding: `${stage.safe.top}px 0 ${stage.safe.bottom}px`,
          }}
        >
          {product}
          {promise}
        </AbsoluteFill>
      )}
      <Sfx sound="notify" at={NOTIF + 4} />
      <Sfx sound="strike" at={STRIKE + 2} />
      <Sfx sound="pop" at={BANK} volume={0.6} />
    </AbsoluteFill>
  );
};
